/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as action from './Store/Pom.action';
import Annotation from '../../../Components/Utility/Annotation/SampleSupplierAnnotation';
import ConfirmationPopup from '../../../Components/ModalPopup/ConfirmationPopup';
import Loader from '../../../Components/Utility/Loader/Loader';
import { toasterMsg } from '../../../Components/Toaster/Toaster';

const EMPTY_IMAGE = {
  annotatedImage: '',
  code: '',
  createdAt: '',
  createdBy: '',
  image: '',
  isNewItem: null,
  isVisibleToSupplier: null,
  modifiedAt: '',
  modifiedBy: '',
  markerObj: {},
  _id: '',
  isArchived: false,
  isDeleted: false,
};

/**
 * Builds the view model the annotation canvas renders from a POM library record.
 * `annotatedImage` falls back to the source image so a POM that has never been
 * annotated still has something to draw on.
 *
 * @param {object|null} pom POM library record, or null when the id is not in the current list.
 * @returns {object} View model matching the EMPTY_IMAGE shape.
 */
const buildCurrentImage = (pom) => {
  if (!pom) {
    return EMPTY_IMAGE;
  }
  return {
    annotatedImage: pom?.annotatedImage ? pom.annotatedImage : pom?.image,
    code: pom?.code,
    createdAt: pom?.createdAt,
    createdBy: pom?.createdBy,
    image: pom?.image,
    isNewItem: pom?.isNewItem,
    isVisibleToSupplier: pom?.isVisibleToSupplier,
    modifiedAt: pom?.modifiedAt,
    modifiedBy: pom?.modifiedBy,
    markerObj: pom?.markerObj ? pom.markerObj : {},
    _id: pom?._id,
    isArchived: pom?.isArchived,
    isDeleted: pom?.isDeleted,
  };
};

function PomAnnotation() {
  const { t } = useTranslation('common');
  const reducer = useSelector((reducer_state) => reducer_state);
  const { PomDetails, UserDetails } = reducer;
  const { pomList } = PomDetails;
  const { userInfo } = UserDetails;
  const history = useHistory();

  const { search } = window.location;
  const params = new URLSearchParams(search);
  const openType = params.get('type') || '';

  /* useState */
  const [obj1Confirm, setObj1Confirm] = useState({});
  const [updateURLConfirm, setUpdateURLConfirm] = useState('');
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [loader, setLoader] = useState(true);
  const [imageEditReplace, setImageEditReplace] = useState(openType);
  const [activeId, setActiveId] = useState(params.get('id'));
  const [imageUnavailable, setImageUnavailable] = useState(false);

  const dispatch = useDispatch();

  const activeIndex = useMemo(() => (pomList || []).findIndex((elem) => elem?._id === activeId), [pomList, activeId]);
  const activePom = activeIndex >= 0 ? pomList[activeIndex] : null;
  const currentImage = useMemo(() => buildCurrentImage(activePom), [activePom]);
  const totalCount = (pomList?.length || 0) - 1;

  const initialExicution = async () => {
    const paramsData = {
      loggedInUserId: userInfo.userId,
    };
    await dispatch(action.getAllPom(paramsData, t));
    setLoader(false);
  };

  useEffect(() => {
    initialExicution();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditModeOn = async (val) => {
    setIsEditOpen(val);
  };

  /**
   * Marks the current POM as having no usable image, so the screen shows its own empty
   * state rather than an annotation canvas built over an image that failed to load.
   */
  const onImageUnavailable = useCallback(() => {
    setIsEditOpen(false);
    setImageUnavailable(true);
  }, []);

  /**
   * Whether the record already holds a stored annotated image. Read from the record rather
   * than from `currentImage`, whose `annotatedImage` falls back to the source image, so a
   * POM annotated for the first time is not mistaken for one with an annotation to lose.
   */
  const hasStoredAnnotation = Boolean(activePom?.annotatedImage);

  const onSave = async (obj, newObj, updatedUrl) => {
    if (hasStoredAnnotation) {
      setObj1Confirm(obj);
      setUpdateURLConfirm(updatedUrl);
      setShowConfirmationPopup(true);
    } else {
      saveAnnotation(obj, newObj, updatedUrl);
    }
  };

  const saveAnnotation = async (obj1, obj2, updateURL) => {
    if (!activePom) {
      toasterMsg(t('POMS.POM_ANNOTATION.NO_IMAGE_FOUND'), 'danger');
      return;
    }
    // Post Api For data in Database
    if (JSON.stringify(obj1.markers) !== JSON.stringify(obj2.markers)) {
      setLoader(true);
      const updateParams = {
        ...activePom,
        annotatedImage: updateURL,
        markerObj: obj1,
        isNewItem: true,
        loggedInUserId: userInfo.userId,
        removeImage: false,
        createdAt: currentImage?.createdAt,
        createdBy: currentImage?.createdBy,
      };

      const result = await dispatch(action.updatePom(updateParams, t));

      if (result) {
        await dispatch(action.getAllPom({ loggedInUserId: userInfo.userId }, t));
      }
      setLoader(false);
      setObj1Confirm({});
      setUpdateURLConfirm('');
      setConfirmLoader(false);
    } else {
      toasterMsg(t('POMS.POM_ANNOTATION.MODIFY_UPDATE'), 'danger');
    }
  };

  const goToNeighbour = (offset) => {
    const neighbour = (pomList || [])[activeIndex + offset];
    if (neighbour) {
      setActiveId(neighbour._id);
      setImageEditReplace('edit');
      setImageUnavailable(false);
    }
  };

  const handleNext = () => {
    goToNeighbour(1);
  };

  const handlePrevious = () => {
    goToNeighbour(-1);
  };

  const sampleImgSet = () => {
    return currentImage?.isNewItem === true ? currentImage?.annotatedImage : currentImage?.image;
  };

  const annotationCalled = () => {
    return currentImage?.image && !imageUnavailable ? (
      <Annotation
        sampleImage={sampleImgSet()}
        sourceImage={currentImage?.image}
        markerObj={currentImage?.markerObj}
        onSaveAnnotation={onSave}
        setIsEditOpen={onEditModeOn}
        openType={imageEditReplace}
        onImageUnavailable={onImageUnavailable}
      />
    ) : (
      <div className="text-center" style={{ padding: '30vh 0' }}>
        {t('POMS.POM_ANNOTATION.NO_IMAGE_FOUND')}
      </div>
    );
  };

  const closeConfirmationsPopup = () => {
    setShowConfirmationPopup(false);
    setConfirmLoader(false);
    setObj1Confirm({});
    setUpdateURLConfirm('');
  };

  const confirmRemoveOldData = () => {
    setConfirmLoader(true);
    saveAnnotation(obj1Confirm, {}, updateURLConfirm);
    setShowConfirmationPopup(false);
  };

  const clearReducer = async () => {
    history.push('/libraries/point-of-measures');
    await dispatch(action.clearAllRecuderRecords());
  };

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title"> {t('POMS.POM_ANNOTATION.TITLE')}</h4>
          {currentImage?.code && (
            <div className="referance-code">
              <span className="ng-binding">{currentImage?.code}</span>
            </div>
          )}
        </div>
        <div>
          <button type="button" aria-label="close" className="cursor-pointer text-dark text-decoration-none p-0 border-0" onClick={clearReducer}>
            <i className="icon-close" />
          </button>
        </div>
      </div>
      {loader ? (
        <Loader />
      ) : (
        <div className="container-fluid">
          <div className="row justify-content-center pt-3" style={{ marginBottom: '50px' }}>
            <div className="col-lg-11">
              {annotationCalled()}
              <button
                aria-label="next"
                className="btn-marker-nav next"
                type="button"
                onClick={handleNext}
                disabled={activeIndex < 0 || activeIndex === totalCount || isEditOpen}
              >
                <span className="iconify" data-icon="grommet-icons:next" />
              </button>
              <button
                aria-label="previous"
                className="btn-marker-nav prev"
                type="button"
                onClick={handlePrevious}
                disabled={activeIndex <= 0 || isEditOpen}
              >
                <span className="iconify" data-icon="grommet-icons:previous" />
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={() => confirmRemoveOldData()}
          title={t('POMS.POM_ANNOTATION.CONFIRM_ACTION_TITLE')}
          message={t('POMS.POM_ANNOTATION.CONFIRM_ACTION_MESSAGE')}
          toggle={closeConfirmationsPopup}
          loading={confirmLoader}
        />
      )}
    </>
  );
}

export default PomAnnotation;
